import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck, Activity, Eye, Trash2, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const ROLES = ["admin", "scorer", "spectator"];

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin", icon: ShieldCheck, desc: "Manage all matches and users" },
  { value: "scorer", label: "Scorer / Umpire", icon: Activity, desc: "Create & score own matches" },
  { value: "spectator", label: "Spectator", icon: Eye, desc: "Read-only access" },
];

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // Add-user dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("scorer");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/auth/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || "Failed to load users"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/auth/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed");
    }
  };

  const deleteUser = async (u) => {
    const ok = window.confirm(
      `Delete ${u.name} (${u.email})?\n\nTheir matches will stay but only admins can edit them. This cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(u.id);
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(`Deleted ${u.name}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const openAdd = () => {
    setNewName("");
    setNewEmail("");
    setNewPassword("");
    setNewRole("scorer");
    setAddOpen(true);
  };

  const submitAdd = async (e) => {
    e?.preventDefault?.();
    if (!newName.trim() || !newEmail.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/users", {
        email: newEmail.trim(),
        name: newName.trim(),
        password: newPassword,
        role: newRole,
      });
      toast.success(`Created ${newName.trim()} (${newRole}) — share login: ${newEmail.trim()}`);
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const RoleIcon = ({ role }) => {
    if (role === "admin") return <ShieldCheck className="w-3 h-3" />;
    if (role === "scorer") return <Activity className="w-3 h-3" />;
    return <Eye className="w-3 h-3" />;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="admin-users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Admin</p>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">
            Users &amp; Roles
          </h1>
        </div>
        <button
          onClick={openAdd}
          data-testid="add-user-btn"
          className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] px-5 py-3 text-xs inline-flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" strokeWidth={3} /> Add User
        </button>
      </div>

      {loading ? (
        <p className="text-muted-foreground uppercase tracking-[0.3em] text-xs">Loading...</p>
      ) : (
        <div className="border border-border bg-card divide-y divide-border">
          {users.map((u) => {
            const isMe = me && me.id === u.id;
            return (
              <div key={u.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`user-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] uppercase tracking-[0.2em] font-bold ${
                      u.role === "admin" ? "border-primary text-primary" :
                      u.role === "scorer" ? "border-foreground text-foreground" :
                      "border-muted-foreground text-muted-foreground"
                    }`}>
                      <RoleIcon role={u.role} /> {u.role}
                    </span>
                    {isMe && <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold">You</span>}
                  </div>
                  <p className="font-display uppercase font-black text-base tracking-tight truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{u.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={u.role}
                    onChange={(e) => changeRole(u.id, e.target.value)}
                    data-testid={`role-select-${u.id}`}
                    className="bg-card border border-border focus:border-primary focus:outline-none px-3 py-2 font-mono text-xs uppercase"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => deleteUser(u)}
                    disabled={isMe || deletingId === u.id}
                    data-testid={`delete-user-${u.id}`}
                    title={isMe ? "Cannot delete your own account" : "Delete user"}
                    className="p-2 text-muted-foreground hover:text-destructive border border-transparent hover:border-destructive transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-transparent disabled:hover:text-muted-foreground"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <p className="px-5 py-6 text-center text-muted-foreground text-xs uppercase tracking-[0.2em]">No users yet</p>
          )}
        </div>
      )}

      {/* ADD USER DIALOG */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              Add User
            </DialogTitle>
            <p className="text-sm text-muted-foreground">Create an account directly. Share the email + password with the user.</p>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4">
            <Field label="Name" type="text" value={newName} onChange={setNewName} testId="new-user-name" required />
            <Field label="Email" type="email" value={newEmail} onChange={setNewEmail} testId="new-user-email" required />
            <Field label="Initial Password" type="text" value={newPassword} onChange={setNewPassword} testId="new-user-password" required />

            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Role</label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {ROLE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const selected = newRole === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setNewRole(opt.value)}
                      data-testid={`new-user-role-${opt.value}`}
                      className={`text-left flex items-center gap-3 px-4 py-3 border transition-colors ${
                        selected ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"
                      }`}
                      style={{ borderRadius: 2 }}
                    >
                      <Icon className={`w-5 h-5 ${selected ? "text-primary" : "text-muted-foreground"}`} strokeWidth={2.5} />
                      <div className="flex-1 min-w-0">
                        <div className={`font-display font-black uppercase text-sm tracking-tight ${selected ? "text-primary" : "text-foreground"}`}>
                          {opt.label}
                        </div>
                        <div className="text-[11px] text-muted-foreground">{opt.desc}</div>
                      </div>
                      <span className={`w-3 h-3 border ${selected ? "bg-primary border-primary" : "border-border"}`} aria-hidden />
                    </button>
                  );
                })}
              </div>
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                className="tactical-btn border border-border px-5 py-2 text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="confirm-add-user-btn"
                className="tactical-btn bg-primary text-primary-foreground px-6 py-3 text-sm disabled:opacity-50"
              >
                {submitting ? "Creating..." : "Create User"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, type, value, onChange, testId, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        required={required}
        className="w-full bg-transparent border border-border focus:border-primary focus:outline-none px-3 py-2 mt-2 font-mono text-sm"
      />
    </div>
  );
}
